import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

import styles from "./IconButton.module.css";
import { Tooltip } from "./Tooltip";

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "aria-label" | "aria-pressed"> {
  /** What the button does, in words. It is the accessible name and the tooltip. */
  label: string;
  /** The glyph — an icon from `icons.tsx`, which is decorative (`aria-hidden`). */
  icon: ReactNode;
  ref?: Ref<HTMLButtonElement>;
  /** A toggle's state, as `aria-pressed` — the only way to set it. Leave it out for a button that is not a toggle. */
  pressed?: boolean;
  /** The key, written the way a person says it („F", „Strg+Z"). Shown in the tooltip, in brackets. */
  shortcut?: string;
}

/**
 * A square button with a glyph and no visible word (BW-2) — and therefore
 * always a tooltip, because the glyph alone is a guess. The name is `label`
 * (`aria-label`), the tooltip says the same plus the shortcut; there is no
 * `title`, which would be a second tooltip of the browser's own on top.
 */
export function IconButton({ label, icon, ref, pressed, shortcut, className, type = "button", ...rest }: IconButtonProps) {
  return (
    <Tooltip text={shortcut ? `${label} (${shortcut})` : label}>
      <button
        {...rest}
        ref={ref}
        type={type}
        className={className ? `${styles.button} ${className}` : styles.button}
        aria-label={label}
        aria-pressed={pressed}
      >
        {icon}
      </button>
    </Tooltip>
  );
}
